// Deployment pipeline visualization for the v2 UI.
//
// A quiet horizontal stepper (dots + connectors) showing where in the pipeline
// a deployment currently is, with a click-to-open details dialog. Restyled from
// the legacy src/components/DeploymentProgressBar.tsx to the v2 Notion palette:
// semantic dot colors, a single gentle pulse on the active step (no spinning
// ring / ripple / gradient-flow), and hairline surfaces.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { getColors, rounded } from '../theme/tokens';
import { duration, easing, growX, reduceMotionSx } from '../theme/motion';
import {
  getStatusHue,
  normalizeStatus,
  statusHues,
  statusLabels,
  tagStyle,
} from '../theme/statusTokens';
import { getCurrentStepIndex, getStepsForType, StepDefinition } from '../lib/deploymentSteps';
import { formatTimestamp, timeAgo } from '../lib/format';
import { isActiveDeploy, isActivelyWatching, isInProgressStatus } from '../lib/grouping';
import DeploymentLogsView from './DeploymentLogsView';
import DeploymentError from './DeploymentError';

/** Which panel the details dialog shows. */
export type DeploymentDialogView = 'progress' | 'logs';

// One gentle box-shadow pulse on the active step — same cadence as StatusDot.
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(11, 118, 209, 0.35); }
  70% { box-shadow: 0 0 0 6px rgba(11, 118, 209, 0); }
  100% { box-shadow: 0 0 0 0 rgba(11, 118, 209, 0); }
`;

export interface DeploymentProgressProps {
  record: DeploymentRecord;
  /**
   * Controlled dialog open state. When provided the component renders ONLY the
   * dialog (no inline stepper trigger) and its open/close is driven by the
   * parent — used by DeploymentRow's "View logs" button. When omitted the
   * component is self-managed: it renders the clickable inline stepper and owns
   * its own open state (the original behavior).
   */
  open?: boolean;
  /** Close handler for controlled mode. */
  onClose?: () => void;
  /** Which tab the dialog opens on (default 'progress'). */
  initialView?: DeploymentDialogView;
}

/**
 * True when this record has enough state to draw a meaningful pipeline — i.e.
 * it's in progress or failed and we can place a current step. Consumers can
 * call this to decide whether to render the stepper at all.
 */
export const hasProgressView = (record: DeploymentRecord): boolean => {
  // An actively-watching deploy has finished its pipeline (status=success) but
  // is still in flight, so we still draw the (completed) flow alongside the
  // watch pill. isActiveDeploy covers both in-progress and watching. (DEVOPS-1629)
  const active = isActiveDeploy(record);
  const failed = record.status?.toLowerCase() === 'failed';
  if (!active && !failed) return false;
  const steps = getStepsForType(record.type);
  return getCurrentStepIndex(steps, record.status, record.stage, record.step) !== -1;
};

const DeploymentProgress: React.FC<DeploymentProgressProps> = ({
  record,
  open,
  onClose,
  initialView = 'progress',
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const detailsOpen = controlled ? open : internalOpen;
  const [view, setView] = useState<DeploymentDialogView>(initialView);

  const handleClose = () => {
    if (controlled) onClose?.();
    else setInternalOpen(false);
  };

  // Reset to the requested tab each time the dialog opens.
  useEffect(() => {
    if (detailsOpen) setView(initialView);
  }, [detailsOpen, initialView]);

  const { type, status, stage, step, timestamp, error } = record;
  // While the post-deploy watch is in flight, append a "Monitor" step so the
  // flow visibly continues past Post. A succeeded deploy lands currentStepIndex at
  // baseSteps.length — exactly the appended step's index — so it reads as the
  // current (active) step. (DEVOPS-1629)
  const watching = isActivelyWatching(record);
  const baseSteps = getStepsForType(type);
  const steps = watching ? [...baseSteps, { name: 'Monitor', shortName: 'Monitor' }] : baseSteps;
  const currentStepIndex = getCurrentStepIndex(baseSteps, status, stage, step);
  const inProgress = isInProgressStatus(status);
  // The watch step is in-flight even though the deploy's own status is success.
  const activeFlow = inProgress || watching;
  const failed = status?.toLowerCase() === 'failed';
  const canonical = normalizeStatus(status);

  const startedAgo = timestamp ? timeAgo(timestamp) : null;

  const stepState = (index: number) => {
    const isCompleted = index < currentStepIndex;
    const isCurrent = index === currentStepIndex;
    const isFailedStep = failed && isCurrent;
    return { isCompleted, isCurrent, isFailedStep };
  };

  const dotColorFor = (index: number) => {
    const { isCompleted, isCurrent, isFailedStep } = stepState(index);
    if (isFailedStep) return statusHues.failed;
    if (isCompleted) return statusHues.success;
    if (isCurrent) return activeFlow ? statusHues.inprogress : getStatusHue(status);
    return null; // pending — outline only
  };

  const renderDot = (stepDef: StepDefinition, index: number) => {
    const { isCompleted, isCurrent, isFailedStep } = stepState(index);
    const color = dotColorFor(index);
    const size = isCurrent ? 11 : 9;

    const stateLabel = isFailedStep
      ? 'Failed'
      : isCompleted
        ? 'Completed'
        : isCurrent
          ? 'In progress'
          : 'Pending';

    const tip = (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
          {stepDef.name}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
          {stateLabel}
        </Typography>
        {isCurrent && startedAgo && (
          <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
            Started {startedAgo}
          </Typography>
        )}
      </Box>
    );

    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 46,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            color: isCurrent ? color || c.inkMuted : c.inkMuted,
            fontWeight: isCurrent ? 600 : 400,
            opacity: isCurrent || isCompleted ? 1 : 0.7,
          }}
        >
          {stepDef.shortName || stepDef.name}
        </Typography>
        <Tooltip title={tip} arrow placement="top">
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: color ?? 'transparent',
              border: `2px solid ${color ?? c.hairline}`,
              transition: 'all 0.25s ease',
              ...(isCurrent && activeFlow && { animation: `${pulse} 1.6s ease-out infinite` }),
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const renderConnector = (index: number) => {
    const isCompleted = index < currentStepIndex;
    if (!isCompleted) {
      return (
        <Box
          key={`conn-${index}`}
          sx={{
            flex: 1,
            minWidth: 16,
            height: 0,
            mt: '6px', // align with dot centers (label row + gap above)
            alignSelf: 'flex-start',
            borderTop: `2px dashed ${c.hairline}`,
          }}
        />
      );
    }
    // Completed: a track that fills green left→right when the step completes.
    return (
      <Box
        key={`conn-${index}`}
        sx={{
          flex: 1,
          minWidth: 16,
          height: 2,
          mt: '5px',
          alignSelf: 'flex-start',
          borderRadius: 1,
          backgroundColor: c.hairline,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundColor: statusHues.success,
            transformOrigin: 'left center',
            animation: `${growX} ${duration.slow}ms ${easing.standard} both`,
            ...reduceMotionSx,
          }}
        />
      </Box>
    );
  };

  return (
    <>
      {!controlled && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="View deployment progress details"
          onClick={() => setInternalOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setInternalOpen(true);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            cursor: 'pointer',
            borderRadius: `${rounded.sm}px`,
            px: 0.5,
            py: 0.5,
            '&:hover': { backgroundColor: c.hoverWash },
            '&:focus-visible': { outline: `2px solid ${c.primary}`, outlineOffset: 2 },
          }}
        >
          {steps.map((stepDef, index) => (
            <React.Fragment key={index}>
              {renderDot(stepDef, index)}
              {index < steps.length - 1 && renderConnector(index)}
            </React.Fragment>
          ))}
        </Box>
      )}

      <Dialog
        open={detailsOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: c.surface, borderRadius: `${rounded.lg}px` },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 0,
            color: c.ink,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>Deployment progress</Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close"
            sx={{ color: c.inkMuted }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Tabs
          value={view}
          onChange={(_, v) => setView(v as DeploymentDialogView)}
          sx={{
            px: 3,
            minHeight: 40,
            borderBottom: `1px solid ${c.hairline}`,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab value="progress" label="Progress" />
          <Tab value="logs" label="Logs" />
        </Tabs>
        <DialogContent>
          {view === 'logs' ? (
            <DeploymentLogsView record={record} enabled={detailsOpen && view === 'logs'} />
          ) : (
          <Stack spacing={2}>
            {/* Status */}
            <Box>
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.75 }}>
                Status
              </Typography>
              {(() => {
                const tg = tagStyle(getStatusHue(status), mode);
                return (
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.3,
                      borderRadius: '6px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      backgroundColor: tg.bg,
                      color: tg.text,
                      border: `1px solid ${tg.border}`,
                    }}
                  >
                    {statusLabels[canonical]}
                  </Box>
                );
              })()}
            </Box>

            {stage && (
              <Box>
                <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
                  Stage
                </Typography>
                <Typography variant="body2" sx={{ color: c.ink }}>
                  {stage}
                </Typography>
              </Box>
            )}

            {step && (
              <Box>
                <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
                  Step
                </Typography>
                <Typography variant="body2" sx={{ color: c.ink }}>
                  {step}
                </Typography>
              </Box>
            )}

            {timestamp && (
              <Box>
                <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.25 }}>
                  Started
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: c.ink }}>
                  <AccessTimeIcon sx={{ fontSize: 15, color: c.inkMuted }} />
                  <Typography variant="body2">
                    {startedAgo} · {formatTimestamp(timestamp)}
                  </Typography>
                </Stack>
              </Box>
            )}

            {failed && error != null && (
              <DeploymentError error={error} record={record} onViewLogs={() => setView('logs')} />
            )}

            {/* All steps */}
            <Box>
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mb: 0.75 }}>
                Pipeline steps
              </Typography>
              <Stack spacing={0.5}>
                {steps.map((stepDef, index) => {
                  const { isCompleted, isCurrent, isFailedStep } = stepState(index);
                  const color = dotColorFor(index);
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1,
                        py: 0.75,
                        borderRadius: `${rounded.sm}px`,
                        backgroundColor: isCurrent ? c.hoverWash : 'transparent',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: color ?? 'transparent',
                          border: `2px solid ${color ?? c.hairline}`,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: c.ink,
                          fontWeight: isCurrent ? 600 : 400,
                          opacity: isCurrent || isCompleted ? 1 : 0.7,
                        }}
                      >
                        {stepDef.name}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      {isCompleted && (
                        <CheckCircleIcon sx={{ fontSize: 16, color: statusHues.success }} />
                      )}
                      {isFailedStep && <ErrorIcon sx={{ fontSize: 16, color: statusHues.failed }} />}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeploymentProgress;
